// src/components/RoomEntry.tsx
import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTopRooms } from "../services/api";
import { isValidRoomId } from "../utils/url"; // Import isValidUrl function
import { Room } from "../../types"; // Import Room type

export function RoomEntry() {
  const [roomInput, setRoomInput] = useState("");
  const [entryError, setEntryError] = useState<string | null>(null);

  const {
    data: topRooms,
    isLoading: isLoadingTopRooms,
    error: topRoomsError,
  } = useQuery<Room[], Error>({
    queryKey: ["topRooms"],
    queryFn: fetchTopRooms,
    staleTime: 60 * 1000, // Cache for 1 minute
  });

  const handleJoinRoom = useCallback(() => {
    const trimmedRoomId = roomInput.trim();
    if (isValidRoomId(trimmedRoomId)) {
      setEntryError(null);
      window.location.pathname = `/${trimmedRoomId}`; // Navigate
    } else {
      setEntryError("Please enter a valid URL (http:// or https://)");
    }
  }, [roomInput]);

  const handleRoomInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRoomInput(e.target.value);
    if (entryError) setEntryError(null);
  };

  const handleRoomInputKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      handleJoinRoom();
    }
  };

  const handleTopRoomClick = (clickedRoomId: string) => {
    window.location.pathname = `/${clickedRoomId}`;
  };

  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-slate-50 p-4 sm:p-6">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-md sm:p-8">
        <h1 className="mb-2 text-center text-2xl font-semibold text-slate-800">
          FST Chat
        </h1>
        {/* Added description */}
        <p className="mb-6 text-center text-sm text-slate-600">
          Welcome to the global live chat for the web. This chat is{" "}
          <strong>100% anonymous</strong>. No messages are ever collected
          or stored –{" "}
          <strong>every message only exists in your browser. (and the ones you are chatting with)</strong>.
        </p>
        <p className="mb-6 text-center text-sm text-slate-600">
          Chat with a YouTube URL and watch the video together with others, or just
          enter any URL to chat with others about it.
        </p>
        <input
          type="text"
          value={roomInput}
          onChange={handleRoomInputChange}
          onKeyPress={handleRoomInputKeyPress}
          placeholder="Enter full URL (http:// or https://)"
          className="mb-4 block w-full rounded-lg border border-slate-300 px-4 py-2 text-base text-slate-700 placeholder-slate-400 transition duration-150 ease-in-out focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
        />
        <button
          onClick={handleJoinRoom}
          className="mb-4 w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-base font-medium text-white transition duration-150 ease-in-out hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Join Room
        </button>
        <button
          onClick={() => handleTopRoomClick("https://fst.chat")}
          className="mb-4 w-full rounded-lg border-2 border-indigo-600 px-4 py-2.5 text-base font-medium text-black transition duration-150 ease-in-out hover:bg-indigo-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Join default Room
        </button>
        {entryError && (
          <div className="mb-4 text-sm text-red-600">{entryError}</div>
        )}
        <a
          href="https://www.icloud.com/shortcuts/b5e132d0ab7e415783868cd40a6b3215"
          target="_blank"
          rel="noopener noreferrer"
          className="mb-6 block text-center text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
        >
          iOS Shortcut to open links in FST Chat
        </a>

        <a
          href="/docs"
          className="mb-6 block text-center text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
        >
          Want to embed a live chat on your website? Check out the docs!
        </a>

        <div>
          <h2 className="mb-2 text-lg font-medium text-slate-700">
            Top Rooms
          </h2>
          {isLoadingTopRooms && (
            <p className="text-slate-500">Loading...</p>
          )}
          {topRoomsError && (
            <p className="text-red-600">
              Error loading top rooms: {topRoomsError.message}
            </p>
          )}
          {topRooms && topRooms.length === 0 && !isLoadingTopRooms && (
            <p className="text-slate-500">No active rooms found.</p>
          )}
          {topRooms && topRooms.length > 0 && (
            <ul className="space-y-1">
              {topRooms.map((room) => (
                <li
                  key={room.roomId}
                  onClick={() => handleTopRoomClick(room.roomId)}
                  className="cursor-pointer rounded-md p-2 text-slate-600 transition duration-150 ease-in-out hover:bg-slate-100 hover:text-slate-800"
                >
                  <span className="block truncate font-medium">
                    {room.roomId}
                  </span>
                  <span className="text-xs text-slate-500">
                    ({room.subscribers} online)
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
